/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import Box from "@mui/material/Box";

import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector, // https://mui.com/x/react-data-grid/filtering/#selectors - Get the id of the rows accessible after the filting process, contains the collapsed children
} from "@mui/x-data-grid-pro";

import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";

import ControlButtons from "./ControlButtons";
import PagingUI from "../../TableContainer/Pagination/PagingUI";
import NotPiAlertDlg from "./Dialogs/NotPiAlertDlg";
import GrantAccessControls from "./GrantAccessControls";
import GrantSuccessConfirmationDlg from "./Dialogs/GrantSuccessConfirmationDlg";
import GrantFailureDlg from "./Dialogs/GrantFailureDlg";
import HttpErrorDlg from "./Dialogs/HttpErrorDlg";
import BusyDlg from "../../UI/Dialogs/BusyDialog";
import "./styles.css";

import {
  JDP_API_PRIVATE_DATA_PARAM,
  collaborators,
  // myProjects,
  grantAccess,
} from "../../../utils/HttpClientProvider";

import cookieUtil from "../../../utils/CookieHandler";

// local helper for sorting collaborators by name
const sortByLower = (a, b) => {
  const nameA = a.name.toUpperCase(); // ignore upper and lowercase
  const nameB = b.name.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  // names must be equal
  return 0;
};

let PAGE_SIZE_ARRAY = [10, 20, 50, 100];
let PAGE_SZIE_DEFAULT = PAGE_SIZE_ARRAY[2];
const PAGE_DEFAULT = 1;

const DLG_NOT_PI = "DLG_NOT_PI";
const DLG_GRANT_SUCCESS = "DLG_GRANT_SUCCESS";
const DLG_GRANT_FAILED = "DLG_GRANT_FAILED";
const DLG_HTTP_ERROR = "DLG_HTTP_ERROR";

// topRect is the app's outer container, excluding the app footer
const DataGridContainer = ({
  topRect,
  setSubmenu,
  currentUser,
  manage,
  rawData,
  loading,
}) => {
  const [gridRows, setGridRows] = useState(rawData);
  const [selectedIds, setSelectedIds] = useState([]); // UI row selection state can be arbitrarily controll by this array
  const [dialogType, setDialogType] = useState(null);
  const [readyForGrant, setReadyForGrant] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gridContainerRect, setGridContainterRect] = useState(null);
  const [activeRows, setActiveRows] = React.useState({
    rows: 0,
    filters: 0,
  }); // eg after filtering
  const apiRef = useGridApiRef();

  const rowsAfterFiltering = () => {
    if (apiRef.current && Object.keys(apiRef.current).length > 0) {
      const filtered = gridFilteredSortedRowIdsSelector(apiRef);
      if (filtered.length !== activeRows.rows) {
        setActiveRows({
          ...activeRows,
          rows: filtered.length,
        });
      }
    }
  };

  useEffect(() => {
    setGridRows(rawData);
  }, [rawData]);

  useEffect(() => {
    rowsAfterFiltering();
  }, [apiRef.current]);

  const history = useHistory();

  const LEADING_COLOMNS = [
    {
      description: "JGI Proposal ID",
      field: "proposalID",
      headerName: "Proposal ID",
      width: 90,
    },
    {
      description: "JGI Final Deliverable ID",
      field: "id",
      headerName: "Project ID",
      width: 90,
    },
    {
      description: "JGI Final Deliverable Project Name",
      field: "projName",
      headerName: "Project Name",
      width: 460,
      autoWidth: true,
      minWidth: 150,
    },
    {
      description: "PI Name",
      field: "PI",
      headerName: "PI Name",
      valueGetter: (params) => {
        return params.row.PI?.name;
      },
      width: 150,
    },
    {
      description: "Project Manager",
      field: "pmName",
      headerName: "Project Manager",
      width: 160,
    },
  ];

  const gridColumns = [
    ...LEADING_COLOMNS,
    {
      description: "JGI Project type",
      field: "prodType",
      headerName: "Product Type",
      width: 240,
      minWidth: 120,
    },
    {
      description: "Project Status",
      field: "projStatus",
      headerName: "Project Status",
      // sortable: false,
      width: 160,
    },
    {
      field: "statusDate",
      headerName: "Status Date",
      width: 110,
    },
    {
      field: "dataUsage",
      headerName: "Data Usage",
      width: 110,
      hide: true,
    },
    {
      field: "visibility",
      headerName: "Data Visibility",
      width: 120,
      // editable: true,
    },
    {
      field: "available_date",
      headerName: "Anticipated Public",
      renderCell: (params) => {
        // console.log("[DEBUG]", params.value);
        const tip =
          params.row.status_name === "Abandoned"
            ? "Project has been abandoned!"
            : params.value === "TBD"
            ? 'This value will be calculated as soon as the status of any child Sequencing Project or Analysis Project transitions to "Complete"'
            : null;
        return (
          <span title={tip} style={tip ? { cursor: "help" } : null}>
            {params.value}
          </span>
        );
      },
      width: 150,
      // editable: true,
    },
  ];

  const manageColumns = [
    ...LEADING_COLOMNS,
    {
      description: "Column of [{name, email, id, ...}, ...].",
      field: "accessList",
      headerName: "Users with Access",
      renderCell: (params) => {
        if (!params.row.accessList) return null;
        const oldUsers = [];
        const newUsers = [];
        params.row.accessList.forEach((d) => {
          if (d.new) {
            newUsers.push(d);
          } else {
            oldUsers.push(d);
          }
        });

        // sort by name
        newUsers.sort(sortByLower);
        oldUsers.sort(sortByLower);
        const alist = [...newUsers, ...oldUsers];

        const content = alist.map((d, idx) => {
          const key = `${d.name.replace(/ /g, "_")}_${idx})`;
          return (
            <Typography
              key={key}
              style={{
                textTransform: "capitalize",
                fontWeight: d.new ? "bold" : "normal",
                padingRight: 2,
                fontSize: params.row.accessList.length > 2 ? 12 : 14,
              }}
            >
              {d.name}{" "}
            </Typography>
          );
        });
        return (
          <Box
            id="data-grid-collaborators"
            sx={{
              maxHeight: 40,
              width: "100%",
              overflowY: "auto",
            }}
          >
            {content}
          </Box>
        );
      },
      width: 250,
    },
    {
      description: "Column of [{name, email, id, ...}, ...].",
      field: "managers",
      headerName: "Project Manager",
      valueGetter: (params) =>
        params.row.managers?.map((d) => d.name).join("; "),
      width: 250,
    },
  ];

  // contact id of login user or the mimic user; user must be login in at thie point, and so currentUser has data
  const mimicId = cookieUtil.getContactIDCookie();

  const user_contect_id =
    mimicId && `${currentUser.mimic?.contact_id}` === mimicId
      ? mimicId
      : currentUser.contact_id;

  const gridContainerBoxRef = useRef(null); // for table height to reach app footer
  const gridRef = useRef(null); // to get current datagrid component width

  const initPageConfig = {
    page: PAGE_DEFAULT,
    pageSize: PAGE_SZIE_DEFAULT,
    pageTotal: 1,
    active: false,
  };
  const [pageConfig, _setPageConfig] = useState(initPageConfig);

  const [gridBoxHeight, setGridBoxHeight] = useState(100);

  // table header, row, and toolbar heights
  const toolUIPadding = 14; // padding: 10px
  const [tableMeta, setTableMeta] = useState({
    density: "standard",
    toolHeight: 36 + 2 * toolUIPadding, // paddings
    headerHeight: 56,
    rowHeight: 52 + 1, // row height with 1px padding
  });

  const _setTableMeta = (density) => {
    const { value, headerHeight, rowHeight } = density;

    if (value !== tableMeta.density) {
      const meta = {
        ...tableMeta,
        density: value,
        headerHeight,
        rowHeight: rowHeight + 1,
      };
      setTableMeta(meta);
    }
  };

  const setPageConfig = (page, pageSize, active = true) => {
    const pageTotal = Math.ceil(
      (activeRows.rows > 0 ? activeRows.rows : gridRows.length) / pageSize
    );
    if (
      page !== pageConfig.page ||
      pageSize !== pageConfig.pageSize ||
      pageTotal !== pageConfig.pageTotal ||
      active !== pageConfig.active
    ) {
      const pconf = { ...pageConfig, pageTotal, page, pageSize, active };
      _setPageConfig(pconf);
    }
  };

  const setHTTPError = (message, status) => {
    setDialogType({
      type: DLG_HTTP_ERROR,
      message,
      status,
    });
  };

  useEffect(() => {
    if (gridRows?.length > 0) {
      const psizeMax = gridRows.length;
      PAGE_SIZE_ARRAY = PAGE_SIZE_ARRAY.filter((d) => d < psizeMax);
      PAGE_SIZE_ARRAY.push(psizeMax);
      if (!PAGE_SIZE_ARRAY.includes(PAGE_SZIE_DEFAULT)) {
        PAGE_SZIE_DEFAULT = psizeMax;
      }
      setPageConfig(PAGE_DEFAULT, PAGE_SZIE_DEFAULT);
    }
  }, [gridRows]);

  const resetHeight = () => {
    if (gridContainerBoxRef?.current && topRect) {
      const rect = gridContainerBoxRef.current.getBoundingClientRect();
      setGridBoxHeight(topRect.bottom - rect.top); // vertical distance from rect.top to app.footer.top
    }
  };

  useEffect(() => {
    if (gridRef.current) {
      const { width, height } = gridRef.current.getBoundingClientRect();
      if (!gridContainerRect || gridContainerRect.width !== width) {
        setGridContainterRect({
          width,
          height,
        });
      }
    }
  }, [gridRef.current, topRect]);

  useEffect(() => {
    resetHeight();
  }, [gridContainerBoxRef.current, topRect]);

  const infoLabelSeparator = (
    <Typography style={{ padding: "0px 12px" }}>|</Typography>
  );
  const infoLabelNum = (num) => (
    <Typography style={{ fontWeight: 600, paddingRight: 4 }}>
      {" "}
      {num}{" "}
    </Typography>
  );

  const toolbarRef = useRef(null);
  const CustomToolbar = () => {
    const toolItemGap = 1;
    return (
      <GridToolbarContainer
        ref={toolbarRef}
        sx={{ padding: `${toolUIPadding}px` }}
      >
        <GridToolbarColumnsButton
          className="data-grid-toolbar-icon"
          sx={{ marginRight: toolItemGap }}
        />
        <GridToolbarFilterButton
          className="data-grid-toolbar-icon"
          sx={{ marginRight: toolItemGap }}
        />
        <GridToolbarDensitySelector
          className="data-grid-toolbar-icon"
          sx={{ marginRight: toolItemGap }}
        />
        <GridToolbarExport className="data-grid-toolbar-icon" />
        <div style={{ display: "flex", marginLeft: "auto" }}>
          {selectedIds?.length > 0 ? (
            <>
              {infoLabelNum(selectedIds.length)}
              <Typography>
                {" "}
                row
                {selectedIds?.length > 1 ? "s" : ""} selected
              </Typography>
              {infoLabelSeparator}
            </>
          ) : null}

          {activeRows.filters > 0 ? (
            <>
              {infoLabelNum(activeRows.rows)}
              <Typography> rows after filter </Typography>
              {infoLabelSeparator}
            </>
          ) : null}

          {rawData?.length > 0 ? (
            <>
              {" "}
              {infoLabelNum(rawData.length)}
              <Typography>
                {} total row{rawData.length > 1 ? "s" : ""}
              </Typography>
            </>
          ) : null}

          <div style={{ marginLeft: 100 }}>
            <PagingUI
              pageSizeLabel="Rows per page"
              pageTotal={pageConfig.pageTotal}
              pageSizeChanged={(size) => {
                setPageConfig(1, size);
              }}
              pageNumChanged={(page) => {
                setPageConfig(page, pageConfig.pageSize);
              }}
              pageSizeArray={PAGE_SIZE_ARRAY}
              pageSizeValue={pageConfig.pageSize}
              pageNumberValue={pageConfig.page}
            />
          </div>
        </div>
      </GridToolbarContainer>
    );
  };

  useEffect(() => {
    if (toolbarRef.current) {
      const toolHeight = toolbarRef.current.clientHeight;
      if (toolHeight !== tableMeta.toolHeight) {
        const meta = {
          ...tableMeta,
          toolHeight,
        };
        setTableMeta(meta);
      }
    }
  }, [toolbarRef.current]);

  const handleRefineAndDownload = () => {
    const url = `/search?q=${selectedIds.join(
      "|"
    )}&f=project_id&${JDP_API_PRIVATE_DATA_PARAM}`;
    history.push(url);
  };

  /* istanbul ignore next */
  const handleDialogCancel = () => {
    setDialogType(null);
  };

  // TODO: return true for co-pi too??
  const isPI = (pid) => `${pid}` === user_contect_id;

  /* istanbul ignore next */
  const prepGrantUI = () => {
    if (dialogType !== null) {
      setDialogType(null);
    }

    setBusy(true);

    if (rawData.length > 0) {
      const toGrantDataMap = {};
      const toGrantData = [];
      let idx = 0;
      rawData.forEach((d) => {
        if (selectedIds.includes(d.id)) {
          if (isPI(d.pi_contact_id)) {
            toGrantDataMap[d.id] = idx;
            toGrantData.push(d);
            idx += 1;
          }
        }
      });

      const forAccessList = toGrantData.filter((d) => !d.accessList);

      if (forAccessList.length > 0) {
        const promise = collaborators(forAccessList.map((d) => d.id));
        promise
          .then((resp) => {
            if (resp.error) {
              setHTTPError(resp.error, resp.status);
            } else if (!resp.data) {
              // no projects returned from BE; no collaborators yet
              setSubmenu("Manage Access");
              setGridRows(toGrantData);
              setReadyForGrant(true);
            } else {
              setSubmenu("Manage Access");
              const { data } = resp;
              if (data.length > 0) {
                data.forEach((d) => {
                  const names = []; // avoid duplicates
                  const collabs = [];
                  d.collaborators.forEach((cd) => {
                    // TODO - here we have cd.level, can be "Final Deliverable", "proposal" etc. what should we do?
                    if (!isPI(cd.contact_id)) {
                      const collabName = `${cd.first_name} ${cd.last_name}`;
                      if (!names.includes(collabName)) {
                        names.push(collabName);
                        collabs.push({
                          name: collabName,
                          email_address: cd.email,
                        });
                      }
                    }
                  });
                  toGrantData[toGrantDataMap[d.project_id]].accessList =
                    collabs;
                  toGrantData[toGrantDataMap[d.project_id]].managers = [
                    {
                      name: `${d.project_manager.first_name} ${d.project_manager.last_name}`,
                      email: d.project_manager.email,
                    },
                  ];
                });
                setGridRows(toGrantData);
                setReadyForGrant(true);
              }
            }
          })
          .finally(() => {
            setBusy(false);
          });
      } else {
        // console.log("[grant ready]", toGrantData);
        setGridRows(toGrantData);
        setReadyForGrant(true);
        setBusy(false);
      }
    }
  };

  // "Manage Access" btn click
  const manageAccessHandler = () => {
    if (readyForGrant) {
      setReadyForGrant(false);
    }

    const piProjects = [];
    const nopiProjects = [];

    rawData.forEach((d) => {
      if (selectedIds.includes(d.id)) {
        if (isPI(d.pi_contact_id)) {
          piProjects.push(d);
        } else {
          nopiProjects.push(d);
        }
      }
    });

    if (nopiProjects.length > 0) {
      setDialogType({ type: DLG_NOT_PI });
    } else {
      prepGrantUI();
    }
  };

  const showDialog = () => {
    switch (dialogType?.type) {
      case DLG_NOT_PI:
        return (
          <NotPiAlertDlg
            uid={user_contect_id}
            data={rawData.filter((d) => selectedIds.includes(d.id))}
            cancelBtnFn={handleDialogCancel}
            proceedBtnFn={prepGrantUI}
          />
        );
      case DLG_GRANT_SUCCESS:
        return (
          <GrantSuccessConfirmationDlg
            data={dialogType?.data}
            continueBtnFn={handleDialogCancel}
          />
        );
      case DLG_GRANT_FAILED:
        return (
          <GrantFailureDlg
            data={dialogType?.data}
            cancelBtnFn={handleDialogCancel}
          />
        );
      case DLG_HTTP_ERROR:
        return (
          <HttpErrorDlg
            message={dialogType.message}
            status={dialogType.status}
            cancelBtnFn={() => {
              setDialogType(null);
            }}
          />
        );
      default:
        return null;
    }
  };

  // const rowsOnPage =
  //   pageConfig.page < pageConfig.pageTotal
  //     ? pageConfig.pageSize
  //     : gridRows.length - pageConfig.pageSize * (pageConfig.page - 1);

  // const tableHeight =
  //   tableMeta.rowHeight * rowsOnPage +
  //   tableMeta.headerHeight +
  //   tableMeta.toolHeight -
  //   1; // the last row without 1px padding

  // const tabelHeightMax = gridBoxHeight > 0 ? gridBoxHeight - 20 : tableHeight;

  /* istanbul ignore next */
  const addUserToAccessListHelper = (id, name, email) => {
    for (let i = 0; i < gridRows.length; i++) {
      const row = gridRows[i];
      if (row.id === id) {
        const map = {};
        row.accessList.forEach((d) => {
          map[d.name] = d;
        });
        if (!Object.keys(map).includes(name)) {
          row.accessList.push({
            name,
            email,
            new: true,
          });
        } else {
          map[name].new = true;
        }

        break;
      }
    }
  };

  /* istanbul ignore next */
  const doGrantAccess = (data) => {
    const payload = {
      project_ids: [],
      orcid_ids: [],
    };
    Object.keys(data.users).forEach((d) => {
      payload.orcid_ids.push(d);
    });
    data.data.forEach((d) => {
      payload.project_ids.push(d.id);
    });

    setBusy(true);
    const promise = grantAccess(payload);
    promise.then((resp) => {
      /**
       * 
       * {
              "status": 200,
              "data": [
                  {
                      "project_id": 1448163,
                      "contacts": [
                          {
                              "orcid_id": "0000-0002-4229-8314",
                              "message": "access has been granted"
                          },
                      ]
                  }
              ]
          }
       */
      const errors = [];
      if (resp.data) {
        const confirmationData = {
          good: {
            users: {},
            projects: [],
          },
          bad: {
            users: [],
            projects: [],
          },
        };
        resp.data.forEach((d) => {
          const fdid = d.project_id;
          if (!d.error) {
            // TODO: not sure if there can be failed case, and if failed case is flagged by d.error!
            if (!confirmationData.good.projects.includes(d.project_id)) {
              confirmationData.good.projects.push(d.project_id);
            }

            d.contacts.forEach((ud) => {
              const { orcid_id } = ud;
              const user = data.users[orcid_id];
              const { first_name, last_name, email } = user;
              const name = `${first_name} ${last_name}`;
              addUserToAccessListHelper(fdid, name, email);
              confirmationData.good.users[orcid_id] = {
                first_name,
                last_name,
                email,
              };
            });

            setGridRows([...gridRows]);
          } else {
            if (!confirmationData.bad.projects.includes(d.project_id)) {
              confirmationData.bad.projects.push(d.project_id);
            }
            d.contacts.forEach((ud) => {
              const { orcid_id } = ud;
              confirmationData.bad.users[orcid_id] = d.error;
            });
            errors.push({ fdid, error: d.error });
          }
        });
        setBusy(false);
        setDialogType({
          type: DLG_GRANT_SUCCESS,
          data: confirmationData,
        });
      }

      // else {
      //   // TODO : error handleing dialog here!
      //   // eslint-disable-next-line no-console
      //   console.log("[doGrandAccess - error]", JSON.stringify(resp));
      // }
    });
  };

  // console.log("[DataGrid gridRows]", gridRows, rawData);
  return (
    <Box>
      {manage && readyForGrant ? (
        <GrantAccessControls
          rows={rawData.filter(
            (d) => selectedIds.includes(d.id) && isPI(d.pi_contact_id)
          )}
          doGrantAccess={doGrantAccess}
          busyHandler={setBusy}
          setHTTPError={setHTTPError}
        />
      ) : (
        <ControlButtons
          selected={selectedIds}
          onManageAccess={manageAccessHandler}
          handleRefineAndDownload={handleRefineAndDownload}
        />
      )}

      <Box
        ref={gridContainerBoxRef}
        sx={{
          // height: 400, // Math.min(tableHeight, tabelHeightMax),
          width: "100%",
          marginTop: "24px",
        }}
      >
        <Box sx={{ display: "flex", height: gridBoxHeight, minHeight: 100 }}>
          {/* need a minHeight to avoid the  "useResizeContainer - The parent DOM element of the data grid has an empty height." error when window height is too small */}
          {loading ? (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          ) : gridRows.length === 0 ? (
            <Typography
              style={{ fontSize: 18, fontStyle: "italic", opacity: 0.5 }}
            >
              No project data{" "}
            </Typography>
          ) : (
            <>
              {/* <Button size="small" onClick={handleSelectFirstVisibleRow}>
                Toggle the selection of the 1st row of the page
              </Button> */}
              <DataGridPro
                apiRef={apiRef}
                ref={gridRef}
                rows={gridRows}
                columns={manage ? manageColumns : gridColumns}
                // components={{ Toolbar: CustomToolbar, Footer: CustomFooter }}
                components={{ Toolbar: CustomToolbar }}
                checkboxSelection
                rowsPerPageOptions={PAGE_SIZE_ARRAY.filter(
                  (d) => typeof d !== "string"
                )}
                paginationModel={{
                  page: pageConfig.active ? pageConfig.page - 1 : 0,
                  pageSize: pageConfig.active ? pageConfig.pageSize : 100,
                }}
                // pageSize={pageConfig?.pageSize}
                // page={pageConfig ? pageConfig.page - 1 : 0} // data-grid page is 0-based
                // onCellClick={handleCellClick}
                // onFilterModelChange={handleFilterModeChange}
                onRowSelectionModelChange={(ids) => {
                  // console.log("[onSelectionModelChange]", ids?.length);
                  setSelectedIds(ids);
                }}
                selectionModel={selectedIds}
                disableColumnMenu // hide disable the kebab menu in column header
                onStateChange={(state) => {
                  _setTableMeta(state.density);
                  const filterCnt = state.filter.filterModel.items.reduce(
                    (sum, d) => (d.value ? sum + 1 : sum),
                    0
                  );

                  if (filterCnt !== activeRows.filters) {
                    setActiveRows({
                      ...activeRows,
                      filters: filterCnt,
                    });
                  }
                  rowsAfterFiltering(); // need to refresh the possible filtered results here
                }}
                getRowHeight={() => "auto"} // auto adjust row content to mutil-lines if too narrow
                pagination={pageConfig.active}
                // hideFooterPagination // keep footer panel to show selection info
                hideFooter
              />
            </>
          )}
        </Box>
      </Box>
      {showDialog()}
      {busy && <BusyDlg message="busy ..." />}
      {/* <Box sx={{ height: 520, width: "100%" }}>
        <DataGridPro
          {...demodata}
          loading={demodata.rows.length === 0}
          rowHeight={38}
          checkboxSelection
          disableRowSelectionOnClick
        />
      </Box> */}
    </Box>
  );
};

export default DataGridContainer;

DataGridContainer.defaultProps = {
  topRect: null,
  currentUser: {},
  rawData: [],
};

DataGridContainer.propTypes = {
  manage: PropTypes.bool.isRequired,
  topRect: PropTypes.shape(),
  currentUser: PropTypes.shape(),
  setSubmenu: PropTypes.func.isRequired,
  rawData: PropTypes.arrayOf(PropTypes.shape()),
  loading: PropTypes.bool.isRequired,
};
